import type { Article } from "../article-types";

export const payrollGrowthArticles: Article[] = [
  {
    slug: "award-compliance-basics",
    title: "Award compliance: the payroll risk owners underestimate",
    metaTitle: "Award Compliance: The Payroll Risk Owners Underestimate",
    description:
      "Most Australian employees are covered by an award under the Fair Work system. What award compliance involves and how to reduce payroll risk.",
    category: "Payroll",
    date: "2026-07-26",
    readingMinutes: 6,
    keywords: [
      "award compliance australia",
      "modern awards explained",
      "payroll compliance small business",
      "fair work award rates",
    ],
    intro:
      "You pay above minimum wage. Job done, you'd think. It isn't. Award compliance means every person gets paid correctly under whichever modern award actually covers their role: base rates, penalties, allowances and overtime included. Most Australian employees sit under an award whether anyone's ever checked or not, and the underpayments that follow are almost never deliberate. They start small, a wrong classification, or a payroll setup that was never really built around the award to begin with. Here's where the risk hides, and what it takes to close it off.",
    sections: [
      { type: "h2", text: "What a modern award covers" },
      {
        type: "p",
        text: "A modern award is the legal document that sets minimum pay and conditions for people in a particular industry or occupation. It sits above the National Employment Standards and below any enterprise agreement or individual contract. Think minimum rates for each classification level, penalty rates for evenings, weekends and public holidays, overtime rules, allowances, and how rosters and breaks are meant to work.",
      },
      {
        type: "p",
        text: "Awards don't stay still, either. The Fair Work Commission reviews minimum rates every year and varies award terms from time to time. A payroll setup that was spot on two years ago can drift out of compliance quietly if nobody's checking it against the current version.",
      },
      { type: "h2", text: "Why owners underestimate the risk" },
      {
        type: "p",
        text: "Paying above the award minimum feels like it should cover you. It often doesn't. A salary can fail to cover the penalties, overtime and allowances an employee would have earned under the award in a given pay period, and some awards require an annual reconciliation to prove the salary left the employee better off. Skip that check and you could be underpaying without any idea it's happening.",
      },
      {
        type: "p",
        text: "Classification trips people up just as often. Awards grade roles by duties and responsibility, not by job title. Hire someone at one level and let them quietly pick up supervisory duties, and they may be entitled to a higher classification and a higher rate. Nobody updates payroll when the duties change, and that gap compounds every single pay run.",
      },
      { type: "h2", text: "Common award compliance mistakes" },
      {
        type: "ul",
        items: [
          "Applying the wrong award, or assuming none applies just because the role happens to sit in an office",
          "Classifying someone at the wrong level and never revisiting it as their duties grow",
          "Paying a flat rate or salary without checking it against what the award would actually pay",
          "Missing allowances: tool, travel, uniform or meal money the award requires",
          "Getting penalty rates and overtime wrong for the hours actually worked",
          "Letting the payroll system fall behind when award rates change each year",
        ],
      },
      {
        type: "callout",
        title: "Underpayment is a business problem, not just an HR problem",
        text: "Back pay claims can reach across several years, and they arrive with interest, super shortfalls and sometimes penalties riding along. The Fair Work Ombudsman pursues underpayment even when the mistake was honest and the business is small. Checking your setup now costs a fraction of unwinding years of wrong pays later.",
      },
      { type: "h2", text: "How to get award compliance under control" },
      { type: "h3", text: "Confirm which award covers each role" },
      {
        type: "p",
        text: "Start with the Fair Work Ombudsman's resources and work out the award and classification for every employee you have. Write your reasoning down as you go. If a role genuinely falls outside every award, note why. And if coverage isn't clear, get advice instead of guessing; guessing is how this problem starts.",
      },
      { type: "h3", text: "Build the award into your payroll software" },
      {
        type: "p",
        text: "Payroll software handles penalty rates, overtime and allowances just fine, but only once it's configured to match the award. Pay items, pay templates and timesheet rules all need to reflect what the award actually says. A generic setup paying one flat rate for every hour worked is exactly where most of these problems begin.",
      },
      { type: "h3", text: "Review at least annually" },
      {
        type: "p",
        text: "Award rates move every year. Put a recurring task in your calendar: update the rates, recheck classifications against what people are actually doing now, and reconcile any annualised salaries against what the award would have paid. Keep a record of each review; it's your evidence if anyone ever asks.",
      },
      { type: "h2", text: "Where NextEra fits" },
      {
        type: "p",
        text: "NextEra's managed payroll service keeps pay runs aligned with current award rates and flags classification questions before they turn into back pay problems, as part of the same monthly bookkeeping rhythm we run for every client. Not sure your current setup would hold up to scrutiny? A Strategic Finance Review is a sensible place to start; it looks at payroll configuration alongside the rest of your finance function.",
      },
    ],
    faq: [
      {
        question: "Does an award apply if I pay above minimum wage?",
        answer:
          "Usually, yes. Paying more than the minimum doesn't switch the award off. It still governs penalties, overtime, allowances and other conditions, and a generous base rate doesn't automatically absorb those entitlements.",
      },
      {
        question: "How do I find out which award covers my employees?",
        answer:
          "The Fair Work Ombudsman website has tools to identify the award and classification for a role based on industry and duties. If coverage is genuinely unclear, get advice rather than guess.",
      },
      {
        question: "What happens if I discover I have underpaid someone?",
        answer:
          "Work out the shortfall, back pay the employee including super on the underpaid amounts, and fix whatever caused it in your payroll setup. The Fair Work Ombudsman publishes guidance on correcting underpayments, and fixing it yourself before anyone complains is viewed far more favourably than waiting to get caught.",
      },
      {
        question: "Do salaried employees need award checks too?",
        answer:
          "Yes, if an award covers their role, and some awards even require a formal annual reconciliation to prove the salary left them better off across penalties, overtime and allowances.",
      },
    ],
    related: [
      "employee-or-contractor",
      "new-employee-payroll-checklist",
      "single-touch-payroll-guide-for-employers",
    ],
  },
  {
    slug: "employee-or-contractor",
    title: "Employee or contractor? Why the difference matters",
    metaTitle: "Employee or Contractor? Why the Difference Matters",
    description:
      "How Australian rules distinguish employees from contractors, why misclassification is costly and where to find authoritative guidance.",
    category: "Payroll",
    date: "2026-07-26",
    readingMinutes: 6,
    keywords: [
      "employee vs contractor australia",
      "contractor misclassification",
      "sham contracting",
      "ato employee contractor rules",
    ],
    intro:
      "Call it whatever you like on the invoice. What decides the question is the whole working relationship, not the label sitting on top of it. Courts, the ATO and the Fair Work Ombudsman all weigh a set of factors together: who controls the work, whether the person can send someone else in their place, who supplies the gear, who carries the commercial risk. Get this wrong and you're looking at back paid entitlements, super shortfalls and penalties, sometimes years after the fact. Here's how the distinction really works, and why it deserves real thought before you engage anyone at all.",
    sections: [
      { type: "h2", text: "Why the classification matters" },
      {
        type: "p",
        text: "Employees come bundled with obligations: PAYG withholding, super guarantee contributions at 12 percent of ordinary time earnings, leave entitlements, award coverage, workers compensation and Single Touch Payroll reporting. Contractors generally invoice you and sort their own tax, though some obligations, super in particular, can still apply to contractors in certain circumstances.",
      },
      {
        type: "p",
        text: "If a worker you've treated as a contractor turns out to be an employee, you could be facing years of back paid super, leave entitlements and possibly penalties on top. The worker agreeing to the arrangement doesn't protect you. The law looks past the agreement to the substance of the relationship.",
      },
      { type: "h2", text: "The multi-factor approach" },
      {
        type: "p",
        text: "There's no single test here. Both the ATO and the Fair Work Ombudsman weigh the relationship as a whole, considering multiple factors together. No one factor decides it, and different factors can point in different directions for the same worker.",
      },
      {
        type: "p",
        text: "Employment tends to look like this: the business controls how, when and where the work happens, the worker can't send someone else in their place, payment is for time worked rather than a result, the business supplies tools and equipment, and the worker carries no real commercial risk. Contracting tends to look the other way: genuine independence in how the work gets delivered, the right to delegate or subcontract, quoting for outcomes rather than hours, bringing your own equipment, and wearing the cost of fixing your own mistakes.",
      },
      {
        type: "callout",
        title: "A contract and an ABN are not enough",
        text: "Calling someone a contractor in writing, or getting them to grab an ABN and send you invoices, doesn't make them one. Deliberately dressing up an employment relationship as contracting is sham contracting, and it carries penalties under the Fair Work Act. What counts is the substance, not the label.",
      },
      { type: "h2", text: "Grey areas to watch" },
      {
        type: "ul",
        items: [
          "Long term contractors who work only for you, to your schedule, on your systems",
          "Former employees you've re-engaged as contractors doing more or less the same job",
          "Workers paid mainly for their labour, where super can apply even to genuine contractors",
          "Tradespeople and consultants who started out independent but have gradually been folded into the team",
          "Workers engaged through their own company or trust, which changes the analysis without removing every obligation",
        ],
      },
      {
        type: "p",
        text: "That labour point catches a lot of owners out. Super law stretches the meaning of employee in some situations, so someone can be a genuine contractor for most purposes and still be owed super. This is exactly the kind of area where checking the ATO's current guidance, or just asking an adviser, actually pays off.",
      },
      { type: "h2", text: "What to do in practice" },
      { type: "h3", text: "Assess before you engage" },
      {
        type: "p",
        text: "Work through the factors honestly before the first invoice or pay run lands, and write your reasoning down while you're at it. Both the ATO and the Fair Work Ombudsman publish guidance and decision tools. If the answer's genuinely unclear, get advice; a professional opinion costs next to nothing next to a misclassification claim.",
      },
      { type: "h3", text: "Review arrangements periodically" },
      {
        type: "p",
        text: "Relationships drift. A contractor who was properly independent three years ago might look an awful lot like an employee today, in everything but the label. Review long running contractor arrangements at least once a year, and again whenever the way you work together changes.",
      },
      { type: "h3", text: "Keep the paperwork consistent" },
      {
        type: "p",
        text: "Whatever the classification, make sure the paperwork, invoicing, insurance and payroll treatment all line up with it. Paying a contractor through payroll, or handing them paid leave, weakens your position and muddies your books at the same time.",
      },
      { type: "h2", text: "Where NextEra fits" },
      {
        type: "p",
        text: "NextEra's managed payroll and monthly bookkeeping keep employee and contractor payments properly separated, with super, STP and withholding handled correctly for each. Got long standing contractor arrangements you've never really looked at twice? Our Strategic Finance Review includes a look at how workers are set up in your books and where the risk sits, so you know what questions to take to the right adviser.",
      },
    ],
    faq: [
      {
        question: "Can a worker choose to be a contractor?",
        answer:
          "No. The classification comes from how the relationship actually works, weighed against multiple factors, not from preference or what's written down.",
      },
      {
        question: "Do I ever have to pay super for contractors?",
        answer:
          "Sometimes, yes. Super law can treat certain contractors as employees for super purposes, particularly where the contract is mainly for their labour. Check the ATO's current guidance or ask an adviser about your specific arrangement.",
      },
      {
        question: "What is sham contracting?",
        answer:
          "Misrepresenting an employment relationship as independent contracting. It's prohibited under the Fair Work Act and can carry significant penalties on top of back paid entitlements.",
      },
      {
        question: "Where can I get an authoritative answer for my situation?",
        answer:
          "Start with ATO and Fair Work Ombudsman guidance; both explain the multi-factor approach in detail. For anything borderline, bring in an employment lawyer or registered tax professional. Classification is fact specific, and generic answers can genuinely mislead you here.",
      },
    ],
    related: ["award-compliance-basics", "payroll-tax-basics", "bookkeeping-for-tradies"],
  },
  {
    slug: "payroll-tax-basics",
    title: "Payroll tax: how it works and when it applies",
    metaTitle: "Payroll Tax in Australia: How It Works and When It Applies",
    description:
      "Payroll tax is a state and territory tax on wages above a threshold. How it works, what counts as wages and when your business should check.",
    category: "Payroll",
    date: "2026-07-26",
    readingMinutes: 5,
    keywords: [
      "payroll tax australia",
      "payroll tax threshold",
      "what is payroll tax",
      "payroll tax registration",
    ],
    intro:
      "Nobody plans to get caught by payroll tax. It just happens: a business hires steadily, the wage bill climbs, and one day it's over a threshold nobody was watching. Payroll tax is a state and territory tax, applied once total wages cross a limit that's different in every jurisdiction, and it has nothing to do with PAYG withholding or income tax. This one's a tax on you as the employer, not on your employees. Because thresholds, rates and rules all vary by state, growing businesses cross into payroll tax territory more often than you'd think. Below is how the tax actually works, and the moments that should send you back to check your numbers.",
    sections: [
      { type: "h2", text: "What payroll tax is, and what it is not" },
      {
        type: "p",
        text: "Each state and territory levies payroll tax on the total taxable wages an employer pays. It's not the tax you withhold from employee pays; that's PAYG withholding, a federal obligation you collect on the ATO's behalf. Payroll tax is an extra cost you carry as the employer, calculated on the wage bill itself once it clears the relevant threshold.",
      },
      {
        type: "p",
        text: "Every state and territory sets its own threshold and rate and runs the tax through its own revenue office. The rules look broadly similar across the country, but the differences matter, so check with the revenue office in each state or territory where you employ people.",
      },
      { type: "h2", text: "What counts as wages" },
      {
        type: "p",
        text: "The definition of wages here is wider than most owners expect. Depending on the jurisdiction, taxable wages can include:",
      },
      {
        type: "ul",
        items: [
          "Salaries, wages, commissions, bonuses and allowances",
          "Super contributions, including the compulsory super guarantee",
          "Fringe benefits provided to employees",
          "Termination payments, in whole or in part",
          "Payments to some contractors, where contractor provisions deem certain arrangements to be wages",
          "Wages paid by related businesses, which can be grouped together",
        ],
      },
      {
        type: "p",
        text: "The contractor provisions and grouping rules are where most businesses get caught. A regular payment to a contractor can count as wages for payroll tax even where that person is a genuine contractor for every other purpose. Run related entities, and their wage bills might be grouped, meaning the combined total gets measured against a single threshold, not one each.",
      },
      {
        type: "callout",
        title: "Growth is the trigger",
        text: "Payroll tax problems rarely start with a decision. They start with growth: a business hires steadily, wages creep past the threshold, and nobody's watching the running total. Revenue offices can look back several years, so a crossing nobody noticed can turn into a serious bill with interest attached. If your wage bill's growing, make the threshold check a standing item on someone's list.",
      },
      { type: "h2", text: "When you need to register" },
      {
        type: "p",
        text: "You generally need to register with the revenue office in each state or territory where you pay wages once your Australia wide wages pass that jurisdiction's threshold. Operate across multiple states and the threshold's typically apportioned, lowering the wage level at which tax kicks in for each place. Registration deadlines and lodgement cycles vary, though most registered employers lodge monthly with an annual reconciliation.",
      },
      {
        type: "p",
        text: "Because thresholds and rates change and differ by jurisdiction, we're deliberately not quoting any here. Check the current figures directly with the relevant state or territory revenue office, or get your accountant or bookkeeper to keep an eye on your position.",
      },
      { type: "h2", text: "Keeping payroll tax manageable" },
      { type: "h3", text: "Track total wages, not just headcount" },
      {
        type: "p",
        text: "The threshold's measured in dollars, including super and other items, not in employee numbers. A small team of well paid specialists can cross the threshold before a much larger team of part timers does. Your bookkeeping should give you a running total of taxable wages across every entity and state you operate in.",
      },
      { type: "h3", text: "Understand grouping early" },
      {
        type: "p",
        text: "Running more than one entity with common ownership or shared employees? Get advice on whether they're grouped for payroll tax before you assume each one gets its own threshold. Grouping is one of the most common findings in a payroll tax audit.",
      },
      { type: "h3", text: "Review contractor payments" },
      {
        type: "p",
        text: "Where you're making regular payments to contractors, check whether your state's contractor provisions deem those payments to be wages. Several exemptions exist and they're specific, so work through it properly with the revenue office guidance or a professional rather than assuming you're covered.",
      },
      { type: "h2", text: "Where NextEra fits" },
      {
        type: "p",
        text: "Because NextEra manages payroll and keeps your books current every month, we can see when total wages are approaching the point where payroll tax deserves a proper look, and flag it before the revenue office does. A Strategic Finance Review also examines how wages, super and contractor payments are recorded across your entities, so the numbers behind that judgement are right.",
      },
    ],
    faq: [
      {
        question: "Is payroll tax the same as PAYG withholding?",
        answer:
          "No. PAYG withholding is federal tax withheld from employee pays and sent to the ATO. Payroll tax is a separate state or territory tax the employer pays on its total wage bill once it clears the local threshold.",
      },
      {
        question: "Do all businesses pay payroll tax?",
        answer:
          "No, plenty of small employers never do, since it only kicks in once total taxable wages exceed the threshold in your state or territory, and thresholds differ by jurisdiction, so check with your local revenue office.",
      },
      {
        question: "Can contractor payments be caught by payroll tax?",
        answer:
          "Yes. Most states and territories have contractor provisions that treat payments under certain contracts as taxable wages, even where the worker's a genuine contractor for other purposes. Specific exemptions do apply, so check them before assuming you're caught either way.",
      },
      {
        question: "What happens if I should have registered but did not?",
        answer:
          "Revenue offices can assess unpaid payroll tax retrospectively, with interest and possible penalties attached. Registering voluntarily the moment you spot the issue tends to be treated far more favourably than waiting for an audit to find it for you, so act promptly.",
      },
    ],
    related: [
      "employee-or-contractor",
      "ias-explained",
      "single-touch-payroll-guide-for-employers",
    ],
  },
  {
    slug: "new-employee-payroll-checklist",
    title: "New employee payroll checklist for Australian employers",
    metaTitle: "New Employee Payroll Checklist for Australian Employers",
    description:
      "Everything to collect and set up before a new employee's first pay: tax file number declaration, super choice, STP and award classification.",
    category: "Payroll",
    date: "2026-07-26",
    readingMinutes: 5,
    keywords: [
      "new employee checklist australia",
      "onboarding payroll setup",
      "new hire forms australia",
      "employee payroll setup",
    ],
    intro:
      "If you're about to hire someone, there's a stack of admin between now and their first pay run. Tax file number declaration. Super choice. The right award classification and rate. A payroll setup wired into Single Touch Payroll. Get these right in week one and you'll dodge most of the payroll problems that otherwise surface months down the track. This checklist walks through each step in the order you'll actually need it.",
    sections: [
      { type: "h2", text: "Before day one" },
      { type: "h3", text: "Confirm the terms in writing" },
      {
        type: "p",
        text: "Issue a written employment agreement covering the role, employment type (full time, part time or casual), pay rate, hours and start date. Fair Work also requires new employees to receive the relevant information statement, usually the Fair Work Information Statement, with casuals needing the casual specific version too. Check the Fair Work Ombudsman site for whichever's current.",
      },
      { type: "h3", text: "Identify the award and classification" },
      {
        type: "p",
        text: "Work out which modern award covers the role and which classification level fits the duties. This one decision drives the minimum pay rate, penalties, allowances and overtime treatment for the whole employment. Write your reasoning down; a classification decision made deliberately is much easier to defend than one made by default.",
      },
      { type: "h3", text: "Check work rights" },
      {
        type: "p",
        text: "Confirm the person's right to work in Australia. For visa holders, verify their work rights and any hour restrictions through the Department of Home Affairs checking service, and keep a record of what you checked and when.",
      },
      { type: "h2", text: "The paperwork to collect" },
      {
        type: "ul",
        items: [
          "Tax file number declaration, completed by the employee so you withhold at the right rate",
          "Superannuation standard choice form, or their stapled super fund details from the ATO if they don't choose a fund",
          "Bank account details for pay, ideally confirmed in writing",
          "Emergency contact and personal details for your records",
          "Any licences, qualifications or registrations the role requires, with expiry dates noted somewhere you'll actually see them",
        ],
      },
      {
        type: "p",
        text: "That stapled fund step matters more than it sounds. If a new employee doesn't nominate a fund, you can't just default them into yours; you have to request their stapled fund details from the ATO first. Pay super into the wrong fund and you might end up paying it again into the right one.",
      },
      { type: "h2", text: "Setting up payroll" },
      { type: "h3", text: "Enter the employee correctly in your software" },
      {
        type: "p",
        text: "Create the employee record with the right employment type, award classification, pay rate, pay cycle and leave entitlements. Casual and permanent settings drive different treatment for leave accruals and loading, so that employment type field isn't a box you tick without thinking.",
      },
      { type: "h3", text: "Connect to Single Touch Payroll" },
      {
        type: "p",
        text: "STP applies to employers of every size, so every pay run needs reporting to the ATO through STP enabled software. First employee? Confirm your software's STP enabled and connected before that first pay goes out, not after.",
      },
      { type: "h3", text: "Set up super correctly" },
      {
        type: "p",
        text: "The super guarantee currently sits at 12 percent of ordinary time earnings. Enter the fund details, confirm the super rate applies to the right pay items, and schedule contributions to actually land on time. Payday super has been legislated to start from 1 July 2026, aligning super payments with each pay run, so confirm the current requirements while you're setting things up.",
      },
      {
        type: "callout",
        title: "The first pay run is your quality check",
        text: "Run the first pay a day or two early, on paper. Check gross pay against the award, tax withheld against the TFN declaration, super at 12 percent of ordinary time earnings, and leave accruing correctly for permanent staff. Ten minutes checking pay one catches errors that would otherwise repeat every pay run for years.",
      },
      { type: "h2", text: "After the first pay" },
      {
        type: "p",
        text: "Confirm the STP report lodged successfully and the payment reached the employee's account. Diarise probation review dates, super payment deadlines and any award rate changes coming up. And if the employee's duties change later, revisit the classification; onboarding is a snapshot, not something you set and forget.",
      },
      {
        type: "p",
        text: "Also confirm workers compensation coverage. Every state and territory requires employers to hold a policy, and a new hire can change your premium or trigger a registration obligation if this is your first employee. Rules vary by state, so check with your local regulator or insurer before assuming you're covered.",
      },
      { type: "h2", text: "Where NextEra fits" },
      {
        type: "p",
        text: "NextEra's managed payroll includes new employee setup: collecting the forms, requesting stapled fund details, entering the record correctly and checking the first pay against the award before it runs. It's part of the same monthly bookkeeping rhythm that keeps the rest of your file clean, and if your whole payroll setup has never had a proper once over, that's exactly the sort of thing a Strategic Finance Review picks up.",
      },
    ],
    faq: [
      {
        question: "What forms does a new employee need to complete in Australia?",
        answer:
          "A tax file number declaration and a superannuation standard choice form at minimum, plus whatever you need for bank details and personal records. You'll also need to provide the Fair Work Information Statement, and the casual version for casual hires.",
      },
      {
        question: "What is a stapled super fund?",
        answer:
          "The existing super fund already linked to an employee, following them between jobs. If a new employee doesn't choose one, you have to request their stapled fund details from the ATO before paying super anywhere else.",
      },
      {
        question: "Do I need Single Touch Payroll for just one employee?",
        answer:
          "Yes. STP applies to employers of every size, including a business with a single employee, and each pay run gets reported to the ATO through STP enabled software.",
      },
      {
        question: "How much super do I pay a new employee?",
        answer:
          "The super guarantee is 12 percent of ordinary time earnings, and it applies to most employees regardless of how much they earn. Contributions need to reach their chosen or stapled fund by the required deadlines, so don't let this one slide.",
      },
    ],
    related: [
      "xero-payroll-setup",
      "award-compliance-basics",
      "single-touch-payroll-guide-for-employers",
    ],
  },
  {
    slug: "termination-and-final-pay",
    title: "Final pay and terminations: getting it right",
    metaTitle: "Final Pay and Terminations: Getting It Right",
    description:
      "What final pay must include when an employee leaves, how unused leave is treated and the payroll steps Australian employers often miss.",
    category: "Payroll",
    date: "2026-07-26",
    readingMinutes: 6,
    keywords: [
      "final pay australia",
      "termination pay entitlements",
      "unused annual leave payout",
      "employee termination payroll",
    ],
    intro:
      "Someone's leaving, and the final pay has to be right. All outstanding wages. Unused annual leave. Anything else owed under their award, agreement or the National Employment Standards, including payment in lieu of notice or redundancy pay where those apply. Final pays are one of the most error prone spots in payroll, purely because several rules collide in a single payment. This guide covers what goes in, how each component gets treated, and the mistakes worth avoiding.",
    sections: [
      { type: "h2", text: "What goes into a final pay" },
      {
        type: "p",
        text: "Every final pay starts with the ordinary stuff: wages for time worked up to the last day, including any penalties, overtime and allowances earned in that final period. From there, what else gets added depends entirely on why and how the employment ended.",
      },
      {
        type: "ul",
        items: [
          "Unused annual leave, paid out on termination including leave loading where the award or agreement provides for it",
          "Unused long service leave, where the employee's reached the entitlement or a pro rata trigger under their state or territory's law",
          "Payment in lieu of notice, if you end employment without them working out their notice period",
          "Redundancy pay, where the role's genuinely redundant and the National Employment Standards or an award or agreement entitles them to it",
          "Any other amounts owed, such as unpaid allowances, commissions or reimbursements",
        ],
      },
      {
        type: "p",
        text: "Unused personal and carer's leave generally doesn't get paid out on termination unless an award, agreement or contract specifically says otherwise. Notice periods and redundancy entitlements come from the National Employment Standards and can be improved by awards and agreements, and the amounts scale with length of service. Check the current Fair Work guidance rather than trusting memory.",
      },
      { type: "h2", text: "Tax and super treatment" },
      {
        type: "p",
        text: "Not every part of a final pay gets taxed the same way. Ordinary wages and unused annual leave have their own withholding treatment, while genuine redundancy payments and certain other amounts can be treated as employment termination payments, with different tax rules and caps applying. Payroll software handles most of this automatically, provided the components are coded correctly, which is exactly why the coding matters so much.",
      },
      {
        type: "p",
        text: "Super is payable on ordinary time earnings, and some final pay components attract it while others don't. What each component is classified as determines the treatment, so a final pay entered as one lump sum on a single pay item is almost guaranteed to be wrong somewhere. The ATO publishes detailed guidance on withholding and super for termination payments if you need to check.",
      },
      {
        type: "callout",
        title: "Slow final pays create disputes",
        text: "Most awards and agreements require final pay within days of employment ending, and a late or obviously wrong final payment is one of the most common triggers for a Fair Work complaint. Even when a departure is messy, pay what's clearly owed straight away and sort any disputed amount separately. A clean final pay closes the file; a messy one reopens it.",
      },
      { type: "h2", text: "The payroll process, step by step" },
      { type: "h3", text: "Confirm the termination details" },
      {
        type: "p",
        text: "Record the last day worked, the reason for termination and whether notice was worked, paid out, or some combination of both. These facts drive every calculation that follows, so get them confirmed in writing before you touch the pay run.",
      },
      { type: "h3", text: "Check leave balances before you pay them" },
      {
        type: "p",
        text: "Payout calculations are only as good as the accrual records sitting behind them. Verify the annual leave balance is current and correct, and check long service leave against your state or territory's rules, which differ on qualifying periods and pro rata entitlements.",
      },
      { type: "h3", text: "Process, report and finalise" },
      {
        type: "p",
        text: "Process the final pay using the correct pay items for each component, report it through Single Touch Payroll, and mark the employee as terminated in your software with the right cessation date and reason. STP finalisation makes sure their income statement reflects the final figures. Then confirm the last super contribution goes out by the deadline.",
      },
      { type: "h2", text: "Special situations to handle carefully" },
      {
        type: "p",
        text: "Redundancies need genuine redundancy to actually exist, plus consultation obligations under awards to be met, and small business employers have their own specific rules, so check Fair Work guidance before you proceed. Dismissals for serious misconduct change the notice position but not the obligation to pay accrued entitlements. Deductions from final pay, for unreturned equipment say, are tightly restricted; don't withhold anything without checking the rules first, because an unlawful deduction creates a liability all of its own.",
      },
      { type: "h2", text: "Where NextEra fits" },
      {
        type: "p",
        text: "Final pays are exactly the kind of infrequent, high stakes calculation that benefits from someone who does them routinely. NextEra's managed payroll handles terminations end to end: component calculations, correct coding, STP finalisation and the super tail that follows. If leave balances in your file have never been verified, a Strategic Finance Review will show you whether the numbers you'd pay out on can be trusted.",
      },
    ],
    faq: [
      {
        question: "Is unused annual leave always paid out when someone leaves?",
        answer:
          "Yes, regardless of why they're leaving; accrued untaken annual leave gets paid out on termination, with leave loading included where the award or agreement provides for it.",
      },
      {
        question: "Is unused sick leave paid out on termination?",
        answer:
          "Generally, no. Accrued personal and carer's leave doesn't get paid out when employment ends unless an award, agreement or employment contract specifically says otherwise.",
      },
      {
        question: "When does final pay have to be made?",
        answer:
          "Timing comes from the applicable award or agreement, and most require payment within days of employment ending. Check the specific instrument covering the employee, and pay whatever's undisputed straight away even if something else is still being sorted out.",
      },
      {
        question: "Do I pay super on a final pay?",
        answer:
          "Super's payable on the components that count as ordinary time earnings, while some termination specific payments don't attract it at all. The treatment depends on how each component's classified, so follow ATO guidance or ask a professional if you're unsure.",
      },
    ],
    related: [
      "leave-entitlements-explained",
      "award-compliance-basics",
      "new-employee-payroll-checklist",
    ],
  },
  {
    slug: "leave-entitlements-explained",
    title: "Leave entitlements: annual, personal and long service",
    metaTitle: "Leave Entitlements: Annual, Personal and Long Service",
    description:
      "A plain guide to annual leave, personal leave and long service leave for Australian employers, and why accurate accruals matter in payroll.",
    category: "Payroll",
    date: "2026-07-26",
    readingMinutes: 6,
    keywords: [
      "leave entitlements australia",
      "annual leave accrual",
      "personal leave rules",
      "long service leave by state",
    ],
    intro:
      "Annual leave, personal leave, long service leave. Same payroll system, three different rulebooks. The National Employment Standards give permanent employees paid annual leave and paid personal and carer's leave, plus unpaid entitlements that reach more broadly, with long service leave layered on top under separate state and territory law. Casuals are generally compensated through casual loading instead of paid leave. Knowing the categories is the easy part for employers; keeping the accruals accurate in payroll, so balances, payouts and provisions can actually be trusted, is where the real work sits.",
    sections: [
      { type: "h2", text: "Annual leave" },
      {
        type: "p",
        text: "Permanent full time and part time employees accrue paid annual leave under the National Employment Standards, with part timers accruing pro rata according to their hours. Leave builds progressively through the year and unused leave carries over; it doesn't expire, and whatever's untaken gets paid out on termination. Some awards and agreements add leave loading on top of the base rate when leave's actually taken.",
      },
      {
        type: "p",
        text: "Awards and agreements can also set rules around when leave gets directed, cashed out or taken in advance. Cashing out in particular has strict conditions, including written agreement and minimum remaining balances, so treat it as a rule check rather than something you agree to informally. Current specifics sit on the Fair Work Ombudsman site.",
      },
      { type: "h2", text: "Personal and carer's leave" },
      {
        type: "p",
        text: "Paid personal and carer's leave covers an employee's own illness or injury and time spent caring for an immediate family or household member. Like annual leave, it builds progressively for permanent employees and unused balances carry over year to year. Unlike annual leave, it generally isn't paid out on termination.",
      },
      {
        type: "p",
        text: "You can ask for reasonable evidence, a medical certificate say, where the award, agreement or workplace policy allows it. There are related entitlements too under the National Employment Standards, including compassionate leave and unpaid carer's leave for casuals; the Fair Work Ombudsman publishes the full set if you need to check.",
      },
      { type: "h2", text: "Long service leave" },
      {
        type: "p",
        text: "Long service leave differs from the other two in an important way: state and territory legislation governs it, not the National Employment Standards, and the rules genuinely differ across the country. Qualifying periods, accrual rates, pro rata entitlements on termination and even how continuous service gets measured all vary by jurisdiction, and some industries run portable long service schemes on top.",
      },
      {
        type: "p",
        text: "Because of that variation, we're not stating any specific years or rates here. If you've got employees approaching long service, check the legislation for your state or territory, or the portable scheme for your industry, and make sure your payroll records can demonstrate continuous service if asked.",
      },
      {
        type: "callout",
        title: "Leave is a liability on your balance sheet",
        text: "Every hour of accrued leave is money your business owes. Growing teams can quietly build a leave liability that surprises owners at sale time, at tax time, or when a long tenured employee finally resigns. Accurate accruals in payroll, plus a periodic look at the larger balances, keep that liability visible instead of hidden.",
      },
      { type: "h2", text: "Where employers go wrong" },
      {
        type: "ul",
        items: [
          "Setting up part timers with full time accrual rates, or the reverse, so balances drift from day one",
          "Paying casuals leave, or converting a casual to permanent without switching their leave settings over",
          "Letting informal leave, days in lieu and the like, live outside the payroll system untracked",
          "Ignoring long service leave until an employee asks about it, then discovering the records can't prove continuous service",
          "Never reconciling leave balances, so termination payouts end up resting on numbers nobody's checked",
        ],
      },
      { type: "h2", text: "Keeping accruals accurate" },
      {
        type: "p",
        text: "Accuracy here is mostly a setup and discipline question. Configure each employee's leave types to match their employment type and hours, process every absence through payroll rather than around it, and reconcile balances periodically against what you'd expect. When hours change, update the accrual settings that same day. If you use timesheets, make sure approved leave flows into the pay run automatically instead of getting keyed in twice.",
      },
      {
        type: "p",
        text: "It's worth reviewing large balances with employees directly too. Encouraging people to actually take their leave shrinks the liability, and most people do better work for having had a proper break.",
      },
      { type: "h2", text: "Where NextEra fits" },
      {
        type: "p",
        text: "NextEra's managed payroll keeps leave accruals set up correctly and processed consistently, so the balances mean something when you need them for a payout, a provision or a sale. If you suspect your current balances are wrong, a Strategic Finance Review can include a leave reconciliation as part of checking the overall health of your payroll records.",
      },
    ],
    faq: [
      {
        question: "Do casual employees get paid leave?",
        answer:
          "Generally not; casual loading compensates for the paid annual and personal leave they don't accrue.",
      },
      {
        question: "Does unused annual leave expire?",
        answer:
          "No, it accumulates year to year and any untaken balance gets paid out when employment ends.",
      },
      {
        question: "Is long service leave the same in every state?",
        answer:
          "No. Separate state and territory laws set it, and qualifying periods, accrual rates and pro rata rules all differ by jurisdiction, with some industries running portable schemes on top. Check the rules that apply where your employees actually work.",
      },
      {
        question: "Can employees cash out annual leave?",
        answer:
          "Sometimes, where the applicable award or agreement allows it and strict conditions are met, a written agreement and a minimum remaining balance among them. Check the specific instrument covering the employee before agreeing to anything.",
      },
    ],
    related: ["termination-and-final-pay", "new-employee-payroll-checklist", "xero-payroll-setup"],
  },
  {
    slug: "payday-super-preparation",
    title: "Payday super: what is changing and how to prepare",
    metaTitle: "Payday Super: What Is Changing and How to Prepare",
    description:
      "Payday super is legislated to start from 1 July 2026, aligning super payments with pay cycles. What it means for cash flow and payroll.",
    category: "Payroll",
    date: "2026-07-26",
    readingMinutes: 5,
    keywords: [
      "payday super",
      "payday super 2026",
      "super guarantee changes",
      "paying super with payroll",
    ],
    intro:
      "Quarterly super's on its way out. Payday super means paying superannuation at the same time as wages instead of in quarterly instalments, and the government has legislated for it to commence from 1 July 2026. Confirm the current status and detail with the ATO or your adviser though, because implementation detail genuinely matters here. For most businesses this change has less to do with payroll mechanics and everything to do with cash flow: super stops being a quarterly bill you save up for and becomes part of every single pay run.",
    sections: [
      { type: "h2", text: "What payday super changes" },
      {
        type: "p",
        text: "Under the old quarterly model, employers could hold super guarantee amounts for up to three months before they had to reach an employee's fund. Payday super closes that gap: the contribution obligation attaches to each payday, with contributions expected to land in employees' funds within a short window after wages go out. The super guarantee rate itself doesn't change under this reform; it stays at 12 percent of ordinary time earnings.",
      },
      {
        type: "p",
        text: "The intent's straightforward. Employees earn super with every pay, funds get it sooner and compound it for longer, and unpaid super becomes visible within weeks rather than months. Already pay super every pay run? This barely touches you. Been leaning on the quarterly cycle as informal working capital? The adjustment is real.",
      },
      { type: "h2", text: "What it means for your business" },
      { type: "h3", text: "Cash flow becomes the main event" },
      {
        type: "p",
        text: "Super stops being a lump sum you assemble at quarter end. Every pay run will need wages, PAYG withholding and super all available at essentially the same time. If your income's lumpy, your debtor days are long, or your revenue's seasonal, model what that does to your cash position across a typical month, because the quarterly buffer is going away.",
      },
      { type: "h3", text: "Late super gets found fast" },
      {
        type: "p",
        text: "With contributions reported through Single Touch Payroll and matched against fund data, the ATO can spot missed or late super quickly. The existing consequences for late super are already unforgiving: the super guarantee charge regime means lost deductibility and extra amounts owed. Under payday super, drift that used to hide inside a quarter surfaces pay run by pay run instead.",
      },
      { type: "h3", text: "Payroll processes need to be tight" },
      {
        type: "p",
        text: "Error corrections, new employee fund details and clearing house processing times all become more time sensitive once the payment window follows every payday. Onboarding steps like collecting super choice forms and requesting stapled fund details from the ATO need to happen before the first pay, not sometime during the first quarter.",
      },
      { type: "h2", text: "How to prepare" },
      {
        type: "ul",
        items: [
          "Confirm the current start date and rules with the ATO or your adviser, since detail can shift between legislation and implementation",
          "Start paying super every pay cycle now, ahead of any deadline, so the cash flow adjustment happens on your terms",
          "Model your cash position with super included in every pay run, especially around BAS and other payment peaks",
          "Check your payroll software and super clearing arrangements can actually support fast, per pay run contributions",
          "Clean up employee super details now; missing fund information and bounced contributions become urgent under a short payment window",
          "Set aside PAYG withholding and super in a separate bank account each pay run so that money never accidentally gets spent",
        ],
      },
      {
        type: "callout",
        title: "Voluntary early adoption is the easy path",
        text: "Nothing's stopping you from paying super each payday before it's mandatory. Businesses that switch early get to iron out software, clearing house and cash flow issues while the quarterly deadlines still provide a safety net. Switch under pressure at the deadline and you get none of that slack.",
      },
      { type: "h2", text: "Where NextEra fits" },
      {
        type: "p",
        text: "NextEra's managed payroll already runs super as part of each pay cycle for clients who want it, and we help everyone else transition ahead of the change: software settings, clearing house setup and a cash flow rhythm that absorbs per payday super without drama. Want a clear picture of what payday super does to your working capital? That's a natural question for a Strategic Finance Review.",
      },
    ],
    faq: [
      {
        question: "When does payday super start?",
        answer:
          "The government has legislated for it to commence from 1 July 2026, though implementation detail can evolve. Confirm the current status and requirements with the ATO or your professional adviser before relying on that date.",
      },
      {
        question: "Does payday super change how much super I pay?",
        answer:
          "No. The super guarantee stays at 12 percent of ordinary time earnings; payday super only changes the timing, aligning payments with each payday instead of quarterly deadlines.",
      },
      {
        question: "What happens if super is paid late under payday super?",
        answer:
          "Late super already triggers the super guarantee charge regime, which is costly and removes deductibility. Under payday super, reporting through payroll systems means late payments get spotted much faster, so the practical risk goes up too.",
      },
      {
        question: "Can I start paying super each payday now?",
        answer:
          "Yes, employers can pay more frequently than the quarterly deadlines any time they like. Starting early lets you adjust cash flow and processes gradually rather than all at once when the rules actually change.",
      },
    ],
    related: [
      "single-touch-payroll-guide-for-employers",
      "xero-payroll-setup",
      "cash-flow-forecasting-guide",
    ],
  },
  {
    slug: "questions-to-ask-before-hiring-a-bookkeeper",
    title: "12 questions to ask before hiring a bookkeeper",
    metaTitle: "12 Questions to Ask Before Hiring a Bookkeeper",
    description:
      "The questions that separate a capable bookkeeper from an expensive mistake: qualifications, registration, software, reporting and communication.",
    category: "Growth",
    date: "2026-07-26",
    readingMinutes: 7,
    keywords: [
      "questions to ask a bookkeeper",
      "hiring a bookkeeper australia",
      "how to choose a bookkeeper",
      "bas agent registration check",
    ],
    intro:
      "Ask the wrong questions before you hire a bookkeeper and you find out everything the hard way, usually months in. Qualifications. BAS agent registration. Who actually does the work. How they handle payroll and month end. What their reporting looks like. How they charge. The answers tell you more than any brochure ever will. A good bookkeeper quietly compounds value every month; a poor one hands you a mess you end up paying twice to fix. These twelve questions, and what to listen for in the answers, should help you tell the difference before you sign anything.",
    sections: [
      { type: "h2", text: "Qualifications and registration" },
      { type: "h3", text: "1. Are you a registered BAS agent?" },
      {
        type: "p",
        text: "Anyone providing BAS services for a fee in Australia, preparing and lodging activity statements included, has to be a registered BAS agent with the Tax Practitioners Board or work under one who is. Registration takes qualifications, experience and ongoing obligations to maintain. You can verify any agent on the TPB's public register, and you should before you sign anything.",
      },
      { type: "h3", text: "2. What qualifications does your team hold?" },
      {
        type: "p",
        text: "Ask about formal qualifications, not just years on the job. A CPA or chartered accountant leading the firm signals a deeper technical bench than bookkeeping certificates alone, and that difference matters the moment your situation stops being simple.",
      },
      { type: "h3", text: "3. Do you carry professional indemnity insurance?" },
      {
        type: "p",
        text: "Registered agents are required to hold appropriate cover, but ask anyway. A professional who hesitates on this one is telling you something worth hearing.",
      },
      { type: "h2", text: "How the work gets done" },
      { type: "h3", text: "4. Who will do my books day to day?" },
      {
        type: "p",
        text: "The person who wins your business isn't always the person who does the work. Ask who handles your file day to day, where they're based, how they're supervised, and what happens when they go on leave. There's no single right answer here, but you deserve a straight one.",
      },
      { type: "h3", text: "5. What software do you work in, and who owns the file?" },
      {
        type: "p",
        text: "You want a bookkeeper fluent in your platform, and you want the subscription and data sitting in your name, not theirs. If a provider insists on owning your file, find out exactly what happens the day you decide to leave.",
      },
      { type: "h3", text: "6. Can you run payroll, including awards and STP?" },
      {
        type: "p",
        text: "Payroll's where bookkeeping errors hurt the most. Ask specifically about award interpretation, Single Touch Payroll, super deadlines and terminations. Vague reassurance here is a warning sign, not an answer.",
      },
      { type: "h3", text: "7. What does your month end process look like?" },
      {
        type: "p",
        text: "A capable bookkeeper can describe a repeatable close: bank reconciliations, payroll and super checks, GST coding review, and reports delivered by a consistent date. No process to describe? You'll get whatever that particular month happened to allow.",
      },
      { type: "h2", text: "Reporting, communication and cost" },
      { type: "h3", text: "8. What will I receive each month, and when?" },
      {
        type: "p",
        text: "Ask to see a sample report pack. You want more than a profit and loss export: some commentary, a balance sheet that's actually reconciled, and numbers arriving on a promised day rather than whenever.",
      },
      { type: "h3", text: "9. How do you communicate, and how fast do you respond?" },
      {
        type: "p",
        text: "Agree the channel and the expected turnaround before you start. Slow, evasive communication is the single most common complaint owners have about their bookkeeper, and it never gets better on its own.",
      },
      { type: "h3", text: "10. How do you charge, and what is excluded?" },
      {
        type: "p",
        text: "Fixed monthly pricing suits most small businesses because it makes the cost predictable and removes that meter running feel of hourly billing. Whatever the model, get inclusions and exclusions in writing, especially around rescue work, software subscriptions and BAS lodgement.",
      },
      { type: "h3", text: "11. How do you work with my accountant?" },
      {
        type: "p",
        text: "Your bookkeeper and tax accountant should be collaborating, not duplicating each other's work. Ask how they hand over at year end and whether they're comfortable being questioned by your accountant. Friction here costs you real money at tax time.",
      },
      { type: "h3", text: "12. Can you tell me about a client you were wrong for?" },
      {
        type: "p",
        text: "An honest answer here shows self awareness about who they actually serve well. Every provider has a lane; the good ones know exactly where theirs is.",
      },
      {
        type: "callout",
        title: "Listen for questions coming back at you",
        text: "The strongest signal in any first conversation is whether the bookkeeper interviews you back. One who asks about your structure, payroll complexity, industry and growth plans is scoping the work properly. One who quotes a price without understanding your business is guessing, and you'll end up paying for that guess later.",
      },
      { type: "h2", text: "What to check before signing" },
      {
        type: "ul",
        items: [
          "Verify BAS agent registration on the Tax Practitioners Board register",
          "Ask for a sample month end report pack, not just a brochure",
          "Get the engagement letter and confirm scope, price, exclusions and exit terms",
          "Confirm the software subscription and data will actually sit in your name",
          "Speak to a current client running a business roughly like yours",
        ],
      },
      { type: "h2", text: "Where NextEra fits" },
      {
        type: "p",
        text: "We built NextEra to answer these questions well: CPA led, registered, fixed monthly pricing, a documented month end close and a report pack you can actually use. Comparing providers? Our Strategic Finance Review is a low pressure way to see how we think about your numbers before you commit to anything ongoing.",
      },
    ],
    faq: [
      {
        question: "Does a bookkeeper need to be registered in Australia?",
        answer:
          "Yes, if they're providing BAS services for a fee, preparing or lodging activity statements included. You can check any agent's registration on the Tax Practitioners Board's public register.",
      },
      {
        question: "Is a cheap bookkeeper a false economy?",
        answer:
          "Often. The cost of fixing miscoded transactions, missed super deadlines or wrong BAS lodgements usually outweighs whatever you saved on the monthly fee, so judge providers on process, qualifications and reporting quality first and compare price after.",
      },
      {
        question: "Should my bookkeeper and accountant be the same firm?",
        answer:
          "It can work either way; one firm reduces handover friction, separate firms give you a second set of eyes. What matters is that the bookkeeping's done to a standard your tax accountant can rely on without rework.",
      },
      {
        question: "What is the biggest red flag when hiring a bookkeeper?",
        answer:
          "A price quoted before they've understood your business. Close behind: no BAS agent registration, no described month end process, and insisting the software file sits in their name rather than yours.",
      },
    ],
    related: [
      "how-much-does-a-bookkeeper-cost-australia",
      "bookkeeper-vs-accountant-australia",
      "signs-you-have-outgrown-your-bookkeeper",
    ],
  },
  {
    slug: "outsourced-vs-in-house-finance",
    title: "Outsourced finance function vs in-house hire: real costs",
    metaTitle: "Outsourced Finance Function vs In-House Hire: Real Costs",
    description:
      "Comparing the true cost of hiring in-house finance staff against an outsourced finance function, including the costs owners forget to count.",
    category: "Growth",
    date: "2026-07-26",
    readingMinutes: 7,
    keywords: [
      "outsourced finance function",
      "outsourced bookkeeping vs in house",
      "cost of hiring a bookkeeper vs outsourcing",
      "in house accountant cost",
    ],
    intro:
      "Salary versus monthly fee. That's the comparison almost every owner makes, and it's the wrong one. What actually matters is the total cost of each option measured against the capability you get for it. An employee's salary is the visible slice of a much bigger number, while an outsourced function trades some day to day presence for breadth of skill and continuity. Below is the full cost picture, so you can compare honestly for wherever your business sits right now.",
    sections: [
      { type: "h2", text: "The true cost of an in-house hire" },
      {
        type: "p",
        text: "Owners anchor on the advertised salary almost every time. The real cost to you as the employer runs materially higher once you add super guarantee at 12 percent of ordinary time earnings, leave entitlements and the cost of covering them, workers compensation premiums, payroll tax where your wage bill clears your state's threshold, software, equipment, training and recruitment. Then there's the cost that never shows up on a payslip: your own time spent managing, reviewing and quality checking the work.",
      },
      {
        type: "p",
        text: "There's a capability ceiling too. One person carries one skill set. A bookkeeper level hire might be excellent at transaction processing and out of their depth on payroll edge cases, entity structures or management reporting. Go the other way, a senior finance manager, and it's expensive, and they'll spend much of their week on work well below their pay grade. Small businesses rarely have a full time role that matches one person's skills edge to edge.",
      },
      { type: "h2", text: "The true cost of outsourcing" },
      {
        type: "p",
        text: "An outsourced finance function charges a fee, typically fixed and monthly, covering an agreed scope: transaction processing, payroll, reconciliations, compliance lodgements and management reporting, with senior review built in. The fee's the headline number, and an honest comparison includes what sits around it: your own time providing information and answering queries, any software billed separately, and fees for out of scope work like one off projects or rescue jobs.",
      },
      {
        type: "p",
        text: "The structural advantages sit in continuity and breadth. You're buying a team, so leave, resignations and sick days stop being your problem, and someone senior reviews the work as a matter of process, not as a favour. The trade offs are real too: nobody's sitting in your office, response times run on an agreement rather than proximity, and a poor provider can hide behind process just as easily as a good one demonstrates it. Scope discipline matters here; anything outside the agreement costs extra, so make sure the agreement reflects how your business actually runs.",
      },
      { type: "h2", text: "Comparing the options honestly" },
      {
        type: "table",
        headers: ["Factor", "In-house hire", "Outsourced function"],
        rows: [
          ["Visible cost", "Salary", "Monthly fee"],
          [
            "Hidden costs",
            "Super, leave, workers comp, payroll tax, software, training, recruitment, your own time managing it",
            "Out of scope work, software billed separately, your time answering queries",
          ],
          [
            "Skill coverage",
            "One person's skill set",
            "A team spanning processing through to senior review",
          ],
          [
            "Continuity",
            "Leave and resignation create real gaps",
            "Provider covers absences internally",
          ],
          [
            "Oversight",
            "You review the work, or nobody does",
            "Senior review built into the process",
          ],
          ["Presence", "In your office, absorbs ad hoc tasks", "Remote, works to an agreed scope"],
          ["Scaling", "Step change; each hire is a big jump", "Scope adjusts as the business does"],
        ],
      },
      {
        type: "callout",
        title: "The riskiest option is the invisible one",
        text: "The most expensive finance function of all is a single unsupervised person, employed or outsourced, whose work nobody ever reviews. Errors compound quietly, and key person risk peaks the exact day they resign. Whichever model you land on, insist on documented processes and a second set of eyes on the numbers.",
      },
      { type: "h2", text: "How to decide for your business" },
      {
        type: "ul",
        items: [
          "Under roughly full time volume of finance work, outsourcing usually wins on cost and capability, since a full time hire spends part of the week underused",
          "If your need spans levels, transaction processing plus management reporting plus payroll expertise, a team beats any single hire you could make",
          "If your business genuinely needs a daily on site presence handling operational tasks beyond finance, an employee might fit better",
          "A hybrid is common and perfectly legitimate: an in-house administrator for daily operational work, with an outsourced function behind them for payroll, compliance and reporting",
          "Whatever you choose, write down the scope, the review process and the reporting deadline, and hold the arrangement to it",
        ],
      },
      {
        type: "p",
        text: "The decision's rarely permanent either way. Plenty of businesses outsource until the finance workload justifies a genuine full time role, then hire into a function that already has clean processes, which makes that eventual hire cheaper and safer than it would otherwise have been.",
      },
      { type: "h2", text: "Where NextEra fits" },
      {
        type: "p",
        text: "NextEra operates as an outsourced finance function for growing Australian businesses: monthly bookkeeping, managed payroll and management reporting with CPA oversight, at a fixed monthly price. Weighing a hire against outsourcing? A Strategic Finance Review will map your actual finance workload and show you what each path would realistically cost, using your own numbers rather than anyone's sales pitch.",
      },
    ],
    faq: [
      {
        question: "Is outsourcing bookkeeping cheaper than hiring?",
        answer:
          "For most small and mid sized businesses, yes, once you count the full employment cost of super, leave, insurance, software, training and management time against a fixed monthly fee. That comparison narrows though as your finance workload gets closer to a genuine full time role.",
      },
      {
        question: "What does an outsourced finance function include?",
        answer:
          "Typically transaction processing, bank reconciliations, payroll, BAS and compliance support, and monthly management reporting, with senior review included. Scope varies by provider though, so get the inclusions and exclusions in writing before you compare prices.",
      },
      {
        question: "Can I combine an in-house person with outsourcing?",
        answer:
          "Yes, and it's a genuinely common structure. An internal administrator handles the daily operational tasks while an outsourced function runs payroll, compliance and reporting behind them, each covering the other's blind spots.",
      },
      {
        question: "When should a business bring finance back in-house?",
        answer:
          "When the workload consistently fills a genuine full time role and the business can actually fund proper oversight of that person. Moving in-house from an outsourced function is easier when documented processes already exist to hand over, so ask your provider about that before you make the switch.",
      },
    ],
    related: [
      "how-much-does-a-bookkeeper-cost-australia",
      "questions-to-ask-before-hiring-a-bookkeeper",
      "finance-function-of-the-next-era",
    ],
  },
  {
    slug: "business-structures-bookkeeping-implications",
    title: "Company, trust or sole trader: bookkeeping implications",
    metaTitle: "Company, Trust or Sole Trader: Bookkeeping Implications",
    description:
      "How your business structure changes day to day bookkeeping: accounts, loans, distributions, compliance obligations and reporting needs.",
    category: "Growth",
    date: "2026-07-26",
    readingMinutes: 7,
    keywords: [
      "business structure bookkeeping",
      "company vs trust vs sole trader",
      "division 7a bookkeeping",
      "trust distribution records",
    ],
    intro:
      "Change your business structure and you change what your bookkeeping has to capture. A sole trader mainly needs clean separation of business and personal transactions. A company has to track director loans and keep proper accounts as a genuinely separate legal entity. A trust needs its distributions and beneficiary entitlements recorded correctly, full stop. Choosing a structure is advice territory for your accountant or lawyer; running the books properly inside whichever one you've picked is everyday discipline, and that's what this article's actually about.",
    sections: [
      { type: "h2", text: "Sole trader: simple, but not casual" },
      {
        type: "p",
        text: "A sole trader and their business are the same legal person, which keeps setup simple and compliance light. The bookkeeping risk is exactly that informality. Business and personal spending blur together, and by year end the file's a mixture your accountant has to untangle at hourly rates you'd rather not pay.",
      },
      {
        type: "p",
        text: "Good sole trader bookkeeping means a dedicated business bank account, every business transaction captured with records to back up the deductions, GST tracked properly if you're registered, and drawings recorded as drawings rather than disguised as expenses. Money you take out isn't a wage and it isn't an expense; it's a reduction of your equity, and coding it correctly keeps your profit figure honest.",
      },
      { type: "h2", text: "Company: a separate entity, treated like one" },
      {
        type: "p",
        text: "A company is a separate legal person. Its money isn't your money, and the bookkeeping has to respect that boundary every single day, not just at year end. Directors who dip into the company account create loans that need to be recorded, documented and managed under the tax rules commonly known as Division 7A; ignore them and they can be treated as unfranked dividends, with tax outcomes nobody enjoys. This is one of the most common and expensive bookkeeping failures in owner managed companies.",
      },
      {
        type: "p",
        text: "Company books also need to handle director salaries through payroll with PAYG withholding and super, dividends declared and recorded properly with franking tracked, and a balance sheet that's actually reconciled, since companies have ASIC as well as the ATO to answer to. Record keeping obligations apply under both tax law and the Corporations Act.",
      },
      { type: "h2", text: "Trust: the paperwork structure" },
      {
        type: "p",
        text: "A trust isn't a separate legal person the way a company is; a trustee, often a company itself, holds and applies assets for beneficiaries under the terms of a trust deed. For bookkeeping purposes, that deed is the rulebook. Distributions of income to beneficiaries need to be determined and documented in line with the deed, generally before year end, and the amounts each beneficiary's entitled to must be recorded accurately.",
      },
      {
        type: "p",
        text: "Practically, trust books need to track beneficiary entitlement accounts, including amounts allocated but not yet paid, keep trustee decisions and minutes on file, and coordinate closely with the accountant around year end resolutions. Sloppy trust bookkeeping doesn't just make for messy reports; it can undermine the tax treatment of the distributions themselves. This is genuinely an area where your bookkeeper and tax accountant need to be talking to each other.",
      },
      {
        type: "callout",
        title: "The structure only works if the books follow it",
        text: "Structures get chosen for asset protection and tax outcomes, but those benefits depend on behaviour matching the paperwork. A company whose director treats the bank account as personal, or a trust with undocumented distributions, can lose in practice everything the structure promised on paper. The bookkeeping is where the structure gets honoured, or quietly dismantled.",
      },
      { type: "h2", text: "What changes across structures" },
      {
        type: "ul",
        items: [
          "Owner payments: drawings for sole traders, salary or dividends or documented loans for companies, distributions for trusts",
          "Chart of accounts: equity sections differ completely across the three structures and should be set up to match",
          "Compliance surface: companies add ASIC and Corporations Act record keeping, trusts add deed compliance and resolutions",
          "Payroll: company directors are typically paid through payroll with PAYG withholding and super, unlike a sole trader's drawings",
          "Multiple entities: groups running a trading company plus a trust need intercompany loans and charges recorded in both files, and reconciled properly",
        ],
      },
      { type: "h2", text: "Changing structure mid stream" },
      {
        type: "p",
        text: "Businesses restructure as they grow, commonly from sole trader to company or trust. From a bookkeeping standpoint that's a new entity with a new file, not a renamed old one: new bank accounts, new payroll registration, GST registration for the new entity, opening balances brought across correctly and a clean cutover date. Keep running transactions through the old structure after the switch and you'll create exactly the kind of tangle restructures are meant to remove. Plan the cutover with your accountant and bookkeeper together, not after the fact.",
      },
      { type: "h2", text: "Where NextEra fits" },
      {
        type: "p",
        text: "NextEra keeps books that match the structure they sit in: director loan accounts watched monthly, trust entitlements recorded the way the accountant needs them, and clean intercompany positions across groups. Restructured recently, or suspect your file still behaves like a sole trader inside a company? A Strategic Finance Review will show you exactly where the books and the structure have drifted apart.",
      },
    ],
    faq: [
      {
        question: "Does my business structure change how bookkeeping is done?",
        answer:
          "Yes. The daily mechanics stay similar, but each structure adds its own requirements: sole traders separate personal and business money, companies track director loans and payroll, and trusts record distributions in line with the trust deed.",
      },
      {
        question: "What is a director loan account and why does it matter?",
        answer:
          "It records money flowing between a director and their company outside salary and dividends. Untracked withdrawals can fall foul of the Division 7A rules and get taxed as unfranked dividends, so the account needs recording, documenting and watching through the year.",
      },
      {
        question: "Which structure has the simplest bookkeeping?",
        answer:
          "A sole trader, since there's one taxpayer and no separate entity accounts to maintain.",
      },
      {
        question: "Do I need a new bookkeeping file if I change structure?",
        answer:
          "Yes. A new entity means a new file, with its own bank accounts, registrations, opening balances and a clean cutover date, and continuing to transact through the old structure afterwards creates errors that are genuinely costly to unwind.",
      },
    ],
    related: [
      "gst-registration-guide-australia",
      "business-record-keeping-requirements",
      "bookkeeper-vs-accountant-australia",
    ],
  },
];
